// src/app/features/warehouse/store/warehouse.reducer.ts
//
// IMPORTANT RULE — cache-aware Load handlers:
// Effects return EMPTY when the cache is valid (no API call, no Success action).
// If the reducer sets loading:true on every Load action regardless, it will NEVER
// go back to false when the effect skips the call -> infinite loading spinner.
// Solution: each Load handler checks isCacheValid() before changing state.

import { createReducer, on } from '@ngrx/store';
import {
  WarehouseState,
  initialWarehouseState,
  AsyncSlice,
  CacheEntry,
  TTL,
  emptySlice,
  isCacheValid,
} from './warehouse.state';
import {
  InvoicesActions,
  InvoiceDetailActions,
  ArticlesActions,
  ArticleTypesActions,
  StockActions,
  StockMovementsActions,
  LowStockActions,
  KomitentsActions,
  KomitentDetailActions,
  SummaryActions,
} from './warehouse.actions';

// ── Helpers ───────────────────────────────────────────────────────────────────

function setLoading<T>(slice: AsyncSlice<T>): AsyncSlice<T> {
  return { ...slice, loading: true, error: null };
}

function setSuccess<T>(slice: AsyncSlice<T>, data: T, ttlMs: number): AsyncSlice<T> {
  const cache: CacheEntry<T> = { data, loadedAt: Date.now(), ttlMs };
  return { data, loading: false, loaded: true, error: null, cache };
}

function setFailure<T>(slice: AsyncSlice<T>, error: string): AsyncSlice<T> {
  return { ...slice, loading: false, error };
}

// ── Reducer ───────────────────────────────────────────────────────────────────

export const warehouseReducer = createReducer(
  initialWarehouseState,

  // ── Invoices (search — no cache guard, switchMap handles cancellation) ─────
  on(InvoicesActions.search, (state): WarehouseState => ({
    ...state,
    invoices: setLoading(state.invoices),
  })),
  on(InvoicesActions.success, (state, { data }): WarehouseState => ({
    ...state,
    invoices: setSuccess(state.invoices, data, TTL.LIST),
  })),
  on(InvoicesActions.failure, (state, { error }): WarehouseState => ({
    ...state,
    invoices: setFailure(state.invoices, error),
  })),
  on(InvoicesActions.clear, (state): WarehouseState => ({
    ...state,
    invoices: emptySlice(),
  })),

  // ── Invoice Detail (keyed by id) ──────────────────────────────────────────
  on(InvoiceDetailActions.load, (state, { id, forceReload }): WarehouseState => {
    const current = state.invoiceDetail[id] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      invoiceDetail: { ...state.invoiceDetail, [id]: setLoading(current) },
    };
  }),
  on(InvoiceDetailActions.success, (state, { id, data }): WarehouseState => ({
    ...state,
    invoiceDetail: {
      ...state.invoiceDetail,
      [id]: setSuccess(state.invoiceDetail[id] ?? emptySlice(), data, TTL.DETAIL),
    },
  })),
  on(InvoiceDetailActions.failure, (state, { id, error }): WarehouseState => ({
    ...state,
    invoiceDetail: {
      ...state.invoiceDetail,
      [id]: setFailure(state.invoiceDetail[id] ?? emptySlice(), error),
    },
  })),
  on(InvoiceDetailActions.clear, (state, { id }): WarehouseState => {
    const map = { ...state.invoiceDetail };
    delete map[id];
    return { ...state, invoiceDetail: map };
  }),

  // ── Articles (search) ─────────────────────────────────────────────────────
  on(ArticlesActions.search, (state): WarehouseState => ({
    ...state,
    articles: setLoading(state.articles),
  })),
  on(ArticlesActions.success, (state, { data }): WarehouseState => ({
    ...state,
    articles: setSuccess(state.articles, data, TTL.LIST),
  })),
  on(ArticlesActions.failure, (state, { error }): WarehouseState => ({
    ...state,
    articles: setFailure(state.articles, error),
  })),
  on(ArticlesActions.clear, (state): WarehouseState => ({
    ...state,
    articles: emptySlice(),
  })),

  // ── Article Types (search) ────────────────────────────────────────────────
  on(ArticleTypesActions.search, (state): WarehouseState => ({
    ...state,
    articleTypes: setLoading(state.articleTypes),
  })),
  on(ArticleTypesActions.success, (state, { data }): WarehouseState => ({
    ...state,
    articleTypes: setSuccess(state.articleTypes, data, TTL.LIST),
  })),
  on(ArticleTypesActions.failure, (state, { error }): WarehouseState => ({
    ...state,
    articleTypes: setFailure(state.articleTypes, error),
  })),
  on(ArticleTypesActions.clear, (state): WarehouseState => ({
    ...state,
    articleTypes: emptySlice(),
  })),

  // ── Stock (search) ────────────────────────────────────────────────────────
  on(StockActions.search, (state): WarehouseState => ({
    ...state,
    stock: setLoading(state.stock),
  })),
  on(StockActions.success, (state, { data }): WarehouseState => ({
    ...state,
    stock: setSuccess(state.stock, data, TTL.LIST),
  })),
  on(StockActions.failure, (state, { error }): WarehouseState => ({
    ...state,
    stock: setFailure(state.stock, error),
  })),
  on(StockActions.clear, (state): WarehouseState => ({
    ...state,
    stock: emptySlice(),
  })),

  // ── Stock Movements (keyed by artId) ──────────────────────────────────────
  on(StockMovementsActions.load, (state, { artId, forceReload }): WarehouseState => {
    const current = state.stockMovements[artId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      stockMovements: { ...state.stockMovements, [artId]: setLoading(current) },
    };
  }),
  on(StockMovementsActions.success, (state, { artId, data }): WarehouseState => ({
    ...state,
    stockMovements: {
      ...state.stockMovements,
      [artId]: setSuccess(state.stockMovements[artId] ?? emptySlice(), data, TTL.DETAIL),
    },
  })),
  on(StockMovementsActions.failure, (state, { artId, error }): WarehouseState => ({
    ...state,
    stockMovements: {
      ...state.stockMovements,
      [artId]: setFailure(state.stockMovements[artId] ?? emptySlice(), error),
    },
  })),
  on(StockMovementsActions.clear, (state, { artId }): WarehouseState => {
    const map = { ...state.stockMovements };
    delete map[artId];
    return { ...state, stockMovements: map };
  }),

  // ── Low Stock (search) ────────────────────────────────────────────────────
  on(LowStockActions.search, (state): WarehouseState => ({
    ...state,
    lowStock: setLoading(state.lowStock),
  })),
  on(LowStockActions.success, (state, { data }): WarehouseState => ({
    ...state,
    lowStock: setSuccess(state.lowStock, data, TTL.LIST),
  })),
  on(LowStockActions.failure, (state, { error }): WarehouseState => ({
    ...state,
    lowStock: setFailure(state.lowStock, error),
  })),
  on(LowStockActions.clear, (state): WarehouseState => ({
    ...state,
    lowStock: emptySlice(),
  })),

  // ── Komitents (search) ────────────────────────────────────────────────────
  on(KomitentsActions.search, (state): WarehouseState => ({
    ...state,
    komitents: setLoading(state.komitents),
  })),
  on(KomitentsActions.success, (state, { data }): WarehouseState => ({
    ...state,
    komitents: setSuccess(state.komitents, data, TTL.LIST),
  })),
  on(KomitentsActions.failure, (state, { error }): WarehouseState => ({
    ...state,
    komitents: setFailure(state.komitents, error),
  })),
  on(KomitentsActions.clear, (state): WarehouseState => ({
    ...state,
    komitents: emptySlice(),
  })),

  // ── Komitent Detail (keyed by id) ─────────────────────────────────────────
  on(KomitentDetailActions.load, (state, { id, forceReload }): WarehouseState => {
    const current = state.komitentDetail[id] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      komitentDetail: { ...state.komitentDetail, [id]: setLoading(current) },
    };
  }),
  on(KomitentDetailActions.success, (state, { id, data }): WarehouseState => ({
    ...state,
    komitentDetail: {
      ...state.komitentDetail,
      [id]: setSuccess(state.komitentDetail[id] ?? emptySlice(), data, TTL.DETAIL),
    },
  })),
  on(KomitentDetailActions.failure, (state, { id, error }): WarehouseState => ({
    ...state,
    komitentDetail: {
      ...state.komitentDetail,
      [id]: setFailure(state.komitentDetail[id] ?? emptySlice(), error),
    },
  })),
  on(KomitentDetailActions.clear, (state, { id }): WarehouseState => {
    const map = { ...state.komitentDetail };
    delete map[id];
    return { ...state, komitentDetail: map };
  }),

  // ── Summary (cached) ─────────────────────────────────────────────────────
  on(SummaryActions.load, (state, { forceReload }): WarehouseState => {
    if (!forceReload && isCacheValid(state.summary.cache)) return state;
    return { ...state, summary: setLoading(state.summary) };
  }),
  on(SummaryActions.success, (state, { data }): WarehouseState => ({
    ...state,
    summary: setSuccess(state.summary, data, TTL.STATIC),
  })),
  on(SummaryActions.failure, (state, { error }): WarehouseState => ({
    ...state,
    summary: setFailure(state.summary, error),
  })),
);
